import { inject, InjectionToken } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { NOTIFICATION_TOKEN, NotificationPermissionProvider } from './notification-permission.token';
import { MODAL_TOKEN, ModalProvider } from './modal.token';
import { PLATFORM_TOKEN, PlatformProvider } from './platform.token';
import { ActionModalComponent } from '../modals';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { notificationPermissionDeniedText, permissionModalButtons } from '../consts';

export const ALERT_PERMISSIONS_TOKEN: InjectionToken<AlertPermissionsProvider> =
  new InjectionToken<AlertPermissionsProvider>('ALERT_PERMISSIONS_TOKEN', {
    factory: (): AlertPermissionsProvider => {
      const notificationProvider: NotificationPermissionProvider = inject(NOTIFICATION_TOKEN);
      const modalProvider: ModalProvider = inject(MODAL_TOKEN);
      const platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);

      return {
        ensureNotificationPermission$(): Observable<boolean> {
          return notificationProvider.isEnabled().pipe(
            switchMap((notifEnabled: boolean) => {
              if (notifEnabled) return of(true);

              return modalProvider
                .showModal$({
                  component: ActionModalComponent,
                  componentProps: {
                    header: notificationPermissionDeniedText.header,
                    message: notificationPermissionDeniedText.message,
                    btnOk: permissionModalButtons.ok,
                    btnCancel: permissionModalButtons.cancel,
                    action: () => openNotificationSettings(platformProvider)
                  },
                  cssClass: 'present-modal'
                })
                .pipe(map(() => false as const));
            })
          );
        }
      };
    }
  });

const openNotificationSettings = (platformProvider: PlatformProvider): Promise<{ status: boolean }> => {
  if (platformProvider.isAndroid)
    return NativeSettings.openAndroid({
      option: AndroidSettings.AppNotification
    });

  return NativeSettings.openIOS({
    option: IOSSettings.Notifications
  });
};

export interface AlertPermissionsProvider {
  ensureNotificationPermission$(): Observable<boolean>;
}
